import { msg } from "@lingui/macro";
import type { IntakeData } from "@/lib/types";
import { i18n } from "@/i18n";
import { getSupabaseBrowserClient } from "@/integrations/supabase/client";
import {
  formatOtherSpecialtyInquiryPlain,
  getOtherSpecialtyReviewRows,
} from "./mailerooPayload";

export { getOtherSpecialtyReviewRows };

/** @deprecated use formatOtherSpecialtyInquiryPlain — kept for imports */
export const formatOtherSpecialtyInquiryBody = formatOtherSpecialtyInquiryPlain;

function parseEdgeFunctionResponse(json: unknown): { ok: boolean; message?: string } {
  if (json && typeof json === "object") {
    const o = json as Record<string, unknown>;
    if (o.success === true) {
      return {
        ok: true,
        message: typeof o.message === "string" ? o.message : undefined,
      };
    }
    if (o.success === false) {
      return {
        ok: false,
        message: String(o.message ?? o.error ?? i18n._(msg`Request failed`)),
      };
    }
    if (
      typeof o.message === "string" &&
      o.message.includes("Submitted successfully")
    ) {
      return { ok: true, message: o.message };
    }
    if (typeof o.error === "string") {
      return { ok: false, message: o.error };
    }
  }
  return { ok: false, message: i18n._(msg`Unexpected response`) };
}

/**
 * Sends the other-specialty inquiry via **Supabase Edge Function** only.
 * Configure `VITE_SUPABASE_URL`, `VITE_SUPABASE_PUBLISHABLE_KEY` (or `VITE_SUPABASE_ANON_KEY`),
 * and `VITE_SUPABASE_OTHER_SPECIALTY_FUNCTION`. Edge uses Maileroo SMTP (secrets on Supabase).
 */
export async function sendOtherSpecialtyInquiryMaileroo(
  data: IntakeData,
): Promise<{ ok: boolean; error?: string }> {
  const supabase = getSupabaseBrowserClient();
  const name = import.meta.env.VITE_SUPABASE_OTHER_SPECIALTY_FUNCTION?.trim() ?? "";

  if (!supabase || !name) {
    return {
      ok: false,
      error: i18n._(
        msg`Email is not configured. Set VITE_SUPABASE_URL, VITE_SUPABASE_PUBLISHABLE_KEY, and VITE_SUPABASE_OTHER_SPECIALTY_FUNCTION.`,
      ),
    };
  }

  const { data: resData, error } = await supabase.functions.invoke(name, {
    body: { intakeData: data },
  });

  if (error) {
    return {
      ok: false,
      error: error.message || i18n._(msg`Could not reach email service.`),
    };
  }

  if (resData == null) {
    return { ok: false, error: i18n._(msg`Empty response from email service.`) };
  }

  const parsed = parseEdgeFunctionResponse(resData);
  return parsed.ok
    ? { ok: true }
    : { ok: false, error: parsed.message ?? i18n._(msg`Send failed`) };
}
